import { useRef, ReactNode, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import EcoButton from 'eco-design/base/eco-button';
import { signupCookie } from 'eco-design-reliance/cookies/signupCookie';
import { useReferralStore, useReferralDispatch } from '../../ReferralProvider';
import Refer, { ReferHandle } from '@/components/Referral/CommonV2/ReferralV2';
import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';
import LoginFormModal from '@/components/LoginFormModal';

interface ReferButtonProps {
    /** 按钮主题 */
    lTheme?: 'white' | 'black';
    /** 按钮圆角 */
    lRadius?: 'ellipse';
    /** 自定义按钮文案，不传则使用默认文案 */
    children?: ReactNode;
    /** 自定义 className */
    className?: string;
    /** entry_position 用于 signupCookie */
    entryPosition?: number;
    /** 是否监听 URL 参数 pop=share 自动弹出邀请框 */
    listenPopShare?: boolean;
}

export default function ReferButton({
    lTheme = 'white',
    lRadius = 'ellipse',
    children,
    className,
    entryPosition = 1,
    listenPopShare = false,
}: ReferButtonProps) {
    const { t } = useLang('referral');
    const { query } = useRouter();
    const referRef = useRef<ReferHandle>(null);
    const { referralInfo, inBlackList, isLogin } = useReferralStore();
    const dispatch = useReferralDispatch();

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [isClient, setIsClient] = useState(false);

    const buttonText = children ?? (isLogin ? t('banner.btn3') : t('banner.btn1'));

    const handleClick = () => {
        if (!isLogin) {
            signupCookie({
                sector_name: referralInfo?.title,
                entry_position: entryPosition,
            });
            setShowLoginModal(true);
        } else {
            referRef.current?.show();
        }

        try {
            gtm.push({
                event: 'uaEvent',
                event_name: 'referral_button',
                position: referralInfo?.title || '',
                button_name: isLogin ? t('banner.btn3') : t('banner.btn1'),
            });
        } catch (e) {
            console.log(e);
        }
    };

    const handleLoginSuccess = () => {
        setShowLoginModal(false);
        // 更新登录状态
        dispatch({ type: 'changeIsLogin', payload: true });
        // 登录成功后显示 Refer 弹窗
        setTimeout(() => {
            referRef.current?.show();
        }, 100);
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsClient(true);
        }
    }, []);

    // 监听 URL 参数 pop=share，已登录且 referralInfo 加载完成后自动弹出邀请框
    useEffect(() => {
        if (listenPopShare && query.pop === 'share' && isLogin && !inBlackList && referralInfo) {
            setTimeout(() => {
                referRef.current?.show();
            }, 100);
        }
    }, [listenPopShare, query.pop, isLogin, inBlackList, referralInfo]);

    return (
        <>
            <EcoButton lRadius={lRadius} lTheme={lTheme} loading={!referralInfo} disabled={inBlackList} onClick={handleClick} className={className}>
                {buttonText}
            </EcoButton>
            <Refer ref={referRef} />

            {isClient &&
                showLoginModal &&
                createPortal(
                    <LoginFormModal
                        visible={showLoginModal}
                        onCancel={() => {
                            setShowLoginModal(false);
                        }}
                        onSuccess={handleLoginSuccess}
                        type="passwordless"
                    />,
                    document.body,
                )}
        </>
    );
}
